import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './CategorizationViewResults.scss';
import ArrButton from '../../common/arr-button/ArrButton';
import {CorpusFilterState, TrainingSet} from '../ViewCategorization';
import {
  useCategorizationProjectNotificationStatus,
  useCategorizationRunProview,
  useCategorizationViewProview,
} from '../../../hooks/categorization/useCategorization';
import {
  initialToast,
  ToastNotificationType,
} from '../../reports/concept-explorer/ConceptExplorer';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import ConfirmationModal from './ConfirmationModal';
import './ConfirmationModal.scss';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  CategorizationProviewParams,
  CategorizationProviewResponse,
} from '../../../api/types/categorization.type';
import CategorizationResult from './CategorizationResult';
import {useSearchParams} from 'react-router-dom';
import ArrWaitToCompleteModalWrapper from '../../reports/standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {cancelBackgroundTask} from '../../../api/endpoints/reportingApi';
import {useSelector} from 'react-redux';

export interface PercentageSliderProps {
  cohesion: number;
  corpusFilter: CorpusFilterState;
  selectedTrainingSet: TrainingSet | null;
  categorizationViewData: CategorizationProviewResponse | null;
  setCategorizationViewData: React.Dispatch<
    React.SetStateAction<CategorizationProviewResponse | null>
  >;
  from: 'client' | 'server';
}

type IRunRequestData = {
  proviewID: number;
  notificationId: number;
};

const attempts = [4, 11, 15];

function maxAllowedAttempts(arr: number[]): number {
  return Math.max(...arr);
}

const CategorizationViewResults = ({
  cohesion,
  corpusFilter,
  selectedTrainingSet,
  categorizationViewData,
  setCategorizationViewData,
  from,
}: PercentageSliderProps) => {
  const {proviewProductionDetails} = useSelector(
    (state: any) => state.categorization
  );
  const [toastNotification, setToastNotification] =
    useState<ToastNotificationType>(initialToast);

  const [confirmationModalDetails, setConfirmationModalDetails] = useState<{
    open: boolean;
    from: string;
  }>({
    open: false,
    from: '',
  });
  const isRunButtonEnabled = useMemo(() => {
    const isCorpusSelected = Object.keys(corpusFilter).some(
      (key: string) => corpusFilter[key as keyof CorpusFilterState].length > 0
    );
    const isTrainingSetSelected =
      selectedTrainingSet && selectedTrainingSet?.ids?.length > 0;
    return isCorpusSelected && isTrainingSetSelected;
  }, [selectedTrainingSet, corpusFilter]);

  const [runRequestData, setRunRequestData] = useState<IRunRequestData | null>(
    null
  );

  const [loading, setLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const proviewIdFromSearch = searchParams.get('proviewId');

  const notificationAttemptDetails = useRef({
    attemptIndex: 0,
    attemptCount: attempts[0],
    currentAttempt: 0,
  });

  const currentElement = useRef<HTMLDivElement>(null);
  const {mutate: categorizationRunProview, isPending} =
    useCategorizationRunProview();

  const {
    data,
    isFetching: fetchingNotification,
    refetch,
  } = useCategorizationProjectNotificationStatus({
    notificationId: runRequestData?.notificationId || null,
  });

  const fetchedOnLoad = useRef(false);
  const {mutate: postCategorizationProview} = useCategorizationViewProview(
    (data) => {
      setCategorizationViewData(data);
      setToastNotification(initialToast);
      setLoading(false);
      fetchedOnLoad.current = false;
    },
    () => {
      setLoading(false);
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'server',
          text: t('categorization.errorTexts.apiError'),
        },
      });
    }
  );
  useEffect(() => {
    if (from === 'client') {
      notificationAttemptDetails.current = {
        attemptIndex: 0,
        attemptCount: attempts[0],
        currentAttempt: 0,
      };
    }
  }, [from]);
  useEffect(() => {
    if (fetchingNotification) return;
    if (data === 'INPROGRESS') {
      setTimeout(() => {
        notificationAttemptDetails.current.currentAttempt += 1;
        if (
          notificationAttemptDetails.current.currentAttempt >=
          notificationAttemptDetails.current.attemptCount
        ) {
          setLoading(false);
          setConfirmationModalDetails({
            open: true,
            from:
              notificationAttemptDetails.current.attemptCount ===
              maxAllowedAttempts(attempts)
                ? 'end-notification'
                : 'continue-notification',
          });
          return;
        }
        refetch();
      }, 2000);
      return;
    } else if (data === 'ERROR') {
      setLoading(false);
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'server',
          text: t('categorization.errorTexts.apiError'),
        },
      });
      notificationAttemptDetails.current = {
        attemptIndex: 0,
        attemptCount: attempts[0],
        currentAttempt: 0,
      };
    } else if (data === 'COMPLETED' && runRequestData?.proviewID) {
      setSearchParams((prev) => {
        prev.set('proviewId', String(runRequestData.proviewID));
        return prev;
      });
      setLoading(false);
    }
  }, [fetchingNotification]);

  useEffect(() => {
    if (
      !fetchedOnLoad.current &&
      proviewIdFromSearch &&
      proviewIdFromSearch !==
        categorizationViewData?.proview?.proviewId?.toString()
    ) {
      fetchCategorizationProview(Number(proviewIdFromSearch));
      fetchedOnLoad.current = true;
    }
  }, [proviewIdFromSearch]);

  function fetchCategorizationProview(proviewID: number) {
    setLoading(true);
    const payload: CategorizationProviewParams = proviewProductionDetails
      ? {
          ...proviewProductionDetails,
          proviewId: proviewID,
          isSourceProduction:
            proviewProductionDetails.isProductionExternalSource ?? false,
        }
      : {
          proviewId: proviewID,
          productionId: 0,
          productionName: '',
          productionSetId: 0,
          securityGroupId: 0,
          isProduction: false,
          isSourceProduction: false,
        };
    postCategorizationProview(payload);
  }

  useEffect(() => {
    if (confirmationModalDetails.open) {
      currentElement.current = document.activeElement as HTMLDivElement;
    } else {
      if (currentElement.current) {
        (currentElement.current as HTMLElement).focus();
        currentElement.current = null;
      }
    }
  }, [confirmationModalDetails]);

  const runCategorization = () => {
    if (cohesion < 30 && !confirmationModalDetails.open) {
      setConfirmationModalDetails({open: true, from: 'categorization'});
      return;
    }
    const analysisSetContexts = [];
    for (const keys in corpusFilter) {
      const selectionArray = corpusFilter[
        keys as keyof CorpusFilterState
      ]?.filter((item) => Number(item.id));
      if (selectionArray.length === 0) continue;
      const analysisSet = {
        type: keys,
        contextIds: selectionArray.map((item) => Number(item.id)),
        contextIdText: selectionArray.map((item) => item.text),
      };
      analysisSetContexts.push(analysisSet);
    }
    if (
      analysisSetContexts.length === 0 ||
      selectedTrainingSet?.ids?.length === 0
    ) {
      setToastNotification({
        show: true,
        type: [ToastType.Error],
        message: {
          from: 'client',
          text: t('categorization.errorTexts.noDataSelected'),
        },
      });
      return;
    }
    const payload = {
      trainingSetId:
        selectedTrainingSet?.ids
          ?.filter((item) => Number(item))
          .map((item) => Number(item)) || [],
      trainingSetType: selectedTrainingSet?.trainingSet?.trainingSetType || '',
      cohesionLevel: cohesion / 100,
      documentCount: 0,
      analysisContainer: {
        analysisSetContexts: analysisSetContexts,
      },
      proviewProductionDetails: proviewProductionDetails ?? {},
    };

    categorizationRunProview(payload, {
      onSuccess: (data) => {
        setRunRequestData({...data});
      },
      onError: () => {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: t('categorization.errorTexts.apiError'),
          },
        });
      },
    });
    currentElement.current = document.getElementsByClassName(
      'focus-result-title'
    )[0] as HTMLDivElement;
    setConfirmationModalDetails({open: false, from: ''});
    setLoading(true);
  };

  const handleConfirmation = useCallback(() => {
    if (confirmationModalDetails.from === 'categorization') {
      runCategorization();
    } else if (confirmationModalDetails.from === 'continue-notification') {
      notificationAttemptDetails.current = {
        ...notificationAttemptDetails.current,
        attemptIndex: notificationAttemptDetails.current.attemptIndex + 1,
        attemptCount:
          attempts[notificationAttemptDetails.current.attemptIndex + 1],
        currentAttempt: 0,
      };
      setLoading(true);
      notificationAttemptDetails.current.currentAttempt += 1;
      refetch();
    }
    currentElement.current = document.getElementsByClassName(
      'focus-result-title'
    )[0] as HTMLDivElement;

    setConfirmationModalDetails({open: false, from: ''});
  }, [confirmationModalDetails.from]);

  const handleCloseConfirmation = useCallback(async () => {
    if (runRequestData?.proviewID && runRequestData?.notificationId) {
      try {
        await cancelBackgroundTask(
          runRequestData?.proviewID,
          runRequestData?.notificationId
        );
      } catch (error: any) {
        setToastNotification({
          show: true,
          type: [ToastType.Error],
          message: {
            from: 'server',
            text: error?.response?.data?.title ?? 'Something went wrong.',
          },
        });
      }
    }
    handleNotification();
  }, [runRequestData]);
  const handleNotification = useCallback(() => {
    setConfirmationModalDetails({open: false, from: ''});
    notificationAttemptDetails.current = {
      attemptIndex: 0,
      attemptCount: attempts[0],
      currentAttempt: 0,
    };
  }, []);

  return (
    <>
      <ArrLoadingSpinner
        overlay={true}
        isOpen={loading || isPending}
        color="secondary"
      />
      {toastNotification.show && (
        <ArrToastNotification
          message={toastNotification.message.text}
          status={toastNotification.type}
          onClose={() => {
            setToastNotification((prev) => ({...prev, show: false}));
          }}
        />
      )}
      <ConfirmationModal
        open={confirmationModalDetails.open}
        handleCancel={handleCloseConfirmation}
        handleApply={handleConfirmation}
        handleNotification={handleNotification}
        from={confirmationModalDetails.from}
      />
      <Box className="view-categorization-result">
        <Box
          className="typo-text subhead4_size Step1-title corpus-title focus-result-title"
          aria-label={t('categorization.viewResults.title')}
          data-testid="vc-view-results-title"
          tabIndex={0}
        >
          {t('categorization.viewResults.title')}
        </Box>
        {categorizationViewData &&
        from === 'server' &&
        categorizationViewData.proview.documentCount ? (
          <CategorizationResult data={categorizationViewData.proview} />
        ) : (
          <Box className="scrollable-content">
            <Box className="results-description-section">
              <Typography
                className="typo-text body3_size run-description"
                tabIndex={0}
                data-testid={`categorization-view-results-description`}>
                {t('categorization.viewResults.description')}
              </Typography>
              <Box>
                <ArrButton
                  disabled={!isRunButtonEnabled}
                  size="medium"
                  className="run-button"
                  onClick={runCategorization}
                  dataTestId="categorization-view-results-run-button-button">
                  {t('categorization.viewResults.runButton')}
                </ArrButton>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default CategorizationViewResults;
